import type { CompositionLegendLayer, CompositionLegendEntry } from './legend';
import type { LineDashPreset, VectorSymbols } from '../styles/model';
import { isImageGraphic } from '../symbols/model';
import type { SymbolBacking } from '../symbols/backing';

export function legendEntryHasBacking(entry: CompositionLegendEntry): boolean {
  const point = entry.symbols?.point;
  const marker = entry.annotationSymbol?.kind === 'marker' ? entry.annotationSymbol : undefined;
  return !!(
    (point && isImageGraphic(point.shape) && point.backing?.enabled) ||
    (marker && isImageGraphic(marker.shape) && marker.backing?.enabled)
  );
}
export function markerLegendLongEdge(symbol: {
  readonly size: number;
  readonly shape: import('../symbols/model').SymbolGraphic;
  readonly backing?: SymbolBacking;
}): number {
  return (
    Math.min(6.5, Math.max(3, 2.3 + symbol.size * 0.24)) *
    2 *
    (isImageGraphic(symbol.shape) && symbol.backing?.enabled ? 2 : 1)
  );
}

const DATA_LEGEND_DASHES: Readonly<Record<LineDashPreset, readonly number[]>> = {
  solid: [],
  dashed: [7, 4],
  shortDash: [4, 3],
  dotted: [1, 3],
  dashDot: [7, 3, 1, 3],
};

/** Round caps must not bridge the gaps of the wider graduated samples. Map dashes are unchanged. */
export function legendLineDashes(preset: LineDashPreset, strokeWidth: number): readonly number[] {
  return DATA_LEGEND_DASHES[preset].map((length, index) =>
    index % 2 ? Math.max(length, strokeWidth + 1) : length,
  );
}

/** Sample units share the established 30 by 18 SVG viewport, independent of map pixels. */
export interface LegendSample {
  readonly pointRadius: number;
  readonly pointOutline: number;
  readonly lineWidth: number;
  readonly height: number;
}

const sampleRange = (values: readonly number[]) =>
  values.reduce(
    (range, value) =>
      value > 0 ? { low: Math.min(range.low, value), high: Math.max(range.high, value) } : range,
    { low: Infinity, high: 0 },
  );
const boundedRange = (
  value: number,
  range: { low: number; high: number },
  minimum: number,
  maximum: number,
) => {
  if (value <= 0) return 0;
  if (range.high === range.low) return Math.min(maximum, Math.max(minimum, value));
  return minimum + ((value - range.low) / (range.high - range.low)) * (maximum - minimum);
};

export function legendSamples(layer: CompositionLegendLayer): readonly LegendSample[] {
  const points = layer.entries.flatMap((entry) =>
    entry.symbols ? [entry.symbols.point.radius] : [],
  );
  const lines = layer.entries.flatMap((entry) => (entry.symbols ? [entry.symbols.line.width] : []));
  const pointRange = sampleRange(points);
  const lineRange = sampleRange(lines);
  // Single symbols retain the familiar compact sample. Class entries use one monotonic range.
  const classified = layer.entries.some(
    (entry) => entry.id.startsWith('class:') || entry.id.startsWith('category:'),
  );
  return layer.entries.map((entry) => {
    const symbols = entry.symbols;
    const basePointRadius = symbols
      ? classified && layer.geometry === 'point' && pointRange.high > pointRange.low
        ? boundedRange(symbols.point.radius, pointRange, 2.75, 8)
        : Math.min(6.5, Math.max(2.75, 2.3 + symbols.point.radius * 0.32))
      : 0;
    const backed = legendEntryHasBacking(entry);
    const pointRadius = backed ? Math.min(13, basePointRadius * 2) : basePointRadius;
    const pointOutline = symbols ? Math.min(3, Math.max(0, symbols.point.outlineWidth * 0.65)) : 0;
    const lineWidth = symbols
      ? classified && layer.geometry === 'line' && lineRange.high > lineRange.low
        ? boundedRange(symbols.line.width, lineRange, 1.2, 7)
        : symbols.line.width <= 0
          ? 0
          : Math.min(7, Math.max(2, symbols.line.width))
      : 0;
    const extent = symbols?.point.shape === 'triangle' ? 1.15 : 1;
    return {
      pointRadius,
      pointOutline,
      lineWidth,
      height: backed
        ? 30
        : layer.geometry === 'point'
          ? Math.max(18, pointRadius * extent * 2 + pointOutline + 1)
          : 18,
    };
  });
}

export function simpleLegendSample(symbols: VectorSymbols): LegendSample {
  const backed = isImageGraphic(symbols.point.shape) && symbols.point.backing?.enabled;
  return {
    pointRadius:
      Math.min(6.5, Math.max(2.75, 2.3 + symbols.point.radius * 0.32)) * (backed ? 2 : 1),
    pointOutline: Math.min(3, Math.max(0, symbols.point.outlineWidth * 0.65)),
    lineWidth: symbols.line.width <= 0 ? 0 : Math.min(7, Math.max(2, symbols.line.width)),
    height: backed ? 30 : 18,
  };
}

export const legendAreaOutline = (width: number): number =>
  width <= 0 ? 0 : Math.min(3, Math.max(0.75, width * 0.7));
